import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Helpers } from '@core/utils/helpers';
import { AppConstants } from '@core/constants/app';
import * as moment from 'moment';
import { TranslatePipe } from '@ngx-translate/core';
import { Summary } from '@core/interfaces/entities/summary';
import { Params } from '@angular/router';
import { FeatureFlagsService } from '@core/services/feature-flags.service';
import { ENABLE_GENDER_AND_IDENTITY_FIELDS, ENABLE_RELATIONSHIP_TO_ENROLLEE_FIELDS } from '@core/constants/feature-flags';


@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  providers: [TranslatePipe],
})
export class SummaryComponent {

  private appSummary : Summary;
  @Input() showSectionNavigation = false;
  @Input() showAgreement = false;

  get summary() {
    return this.appSummary;
  }

  @Input() set summary(data: Params) {
    const defaultValue = '-';
    const formFieldsPrefix = `common.formFieldsValues`;
    const yes = this.translate.transform(`${formFieldsPrefix}.defaultRadio.yes`);
    const no = this.translate.transform(`${formFieldsPrefix}.defaultRadio.no`);
    const compiledResidentialAddr = `${data.residentialAddress}
          ${data.residentialAddressExt ? ' ' + data.residentialAddressExt : ''} ${data.residentialCity},
           ${data.residentialState} ${data.residentialZipcode}`;
    const compiledMailingAddress = !data.mailingAddressIsDifferent
      ? compiledResidentialAddr
      : `${data.mailingAddress}
          ${data.mailingAddressExt ? ' ' + data.mailingAddressExt : ''} ${data.mailingCity},
           ${data.mailingState} ${data.mailingZipcode}`;
    const compiledRepAddr = !data.signedByRep
      ? defaultValue
      : `${data.repAddress}
          ${data.repAddressExt ? ' ' + data.repAddressExt : ''} ${data.repCity},
           ${data.repState} ${data.repZipcode}`;
    const raceChoices = this.translate.transform(
      `${this.applicantInfoTranslationPrefix}.content.ethnicitySection.subsections.race.options`
    );
    const ethnicityChoices = this.translate.transform(`${this.applicantInfoTranslationPrefix}.content.ethnicitySection.subsections.ethnicity.options`);
    const mappedRaces = data.raceAnswers?.reduce((result, key) => !key ? result : `${result}<li>${raceChoices[key].label} </li>`, ``);
    const mappedEthnicity = data.ethnicityAnswers?.reduce((result, key) => !key ? result : `${result}<li>${ethnicityChoices[key].label} </li>`, ``);
    const aoeReasonChoices = this.translate.transform(`common.formFieldsValues.attestationRadio`);

    this.appSummary = {
      applicantInfo: [
        {
          firstName: data.firstName,
          middleInitial: data.middleInitial || defaultValue,
          lastName: data.lastName,
          sex: this.translate.transform(`${formFieldsPrefix}.sexRadio.${data.sex.charAt(0)}`),
          dob: moment(data.dob).format('MM/DD/YYYY'),
        },
        {
          residentialAddress: compiledResidentialAddr,
        },
        {
          mailingAddress: compiledMailingAddress,
        },
        {
          homePhone: data.contactHomePhone,
          cellPhone: data.contactCellPhone || defaultValue,
          email: data.contactEmail || defaultValue,
        },
        {
          ethnicityAnswers:  mappedEthnicity ? `<br><ul>${mappedEthnicity}</ul>` : defaultValue,
          raceAnswers:  mappedRaces ? `<br><ul>${mappedRaces}</ul>` : defaultValue,
        },
        {
          signedByRep: data.signedByRep ? yes : no,
          repFirstName: data.repFirstName || defaultValue,
          repLastName: data.repLastName || defaultValue,
          phoneNumber: data.repPhone || defaultValue,
          relationship: data.repRelationship || defaultValue,
          address: compiledRepAddr,
        },
        ...[this.showGenderAndIdentityField ? {
          gender: data.gender ? this.translate.transform(`${this.applicantInfoTranslationPrefix}.content.genderSection.options.${data.gender}.label`) : defaultValue,
          genderOther: data.genderOther ? `: ${data.genderOther}` : defaultValue,
          selfIdentify: data.selfIdentify ? this.translate.transform(`${this.applicantInfoTranslationPrefix}.content.selfIdentifySection.options.${data.selfIdentify}.label`) : defaultValue,
          selfIdentifyOther: data.selfIdentifyOther ? `: ${data.selfIdentifyOther}` : defaultValue,} : null],
        ...[this.showRelationshipToEnrolleeField ? {
          individualRepRelationship: data.individualRepRelationship ? this.translate.transform(`${this.applicantInfoTranslationPrefix}.content.relationshipSection.options.${data.individualRepRelationship}.label`) : defaultValue,} : null],
      ],

      eligibility: {
        zipCode: data.residentialZipcode,
        medicare: data.hasMedicare ? yes : no,
        prescription: data.hasPrescription ? yes : no,
        medicaid: data.hasMedicaid ? yes : no,
        assistance: data.assistanceRequired ? yes : no,
      },

      attestationOfEligibility: {
        reason: data.aoeSelections ? this.resolveAOEReasonDisplay(data.aoeSelections, aoeReasonChoices) : `${aoeReasonChoices[data.reason]} ${data.reasonDate ? moment(data.reasonDate).format('MM/DD/YYYY') : ''} ${data.reasonDescr ? ': ' + data.reasonDescr : ''}`
      },

      plan: {
        planId: `${data.planSelection.name}[ID# ${data.planSelection.hfId}]`,
        planPremium: `$${data.planSelection.premium.toFixed(2)}${data.planSelection.hasDisclaimer ? '†' : ''}`,
      },

      language: `${this.translate.transform(`${formFieldsPrefix}.langRadio.${data.preferredLanguage}`)}/${this.translate.transform(`${formFieldsPrefix}.specialFormatRadio.${data.preferredPrint}`)}`,

      medicareInfo: [
        {
          number: data.medicareNumber,
        },
        {
          otherCoverage: data.medicareHasOtherCoverage ? yes : no,
          otherCoverageName: data.medicareOtherCoverageName || defaultValue,
          coverageId: data.medicareCoverageId || defaultValue,
          coverageGroup: data.medicareCoverageGroup || defaultValue,
        },
        {
          maritalStatus: this.translate.transform(`${formFieldsPrefix}.maritalStatus.${data.medicareMaritalStatus}`) || defaultValue,
          employment: data.medicareIsEmployed ? yes : no,
          spouseIsEmployed: data.medicareSpouseIsEmployed !== null ? data.medicareSpouseIsEmployed ? yes : no : defaultValue,
          isSalaryBelowLimit: data.medicareIsSalaryBelowLimit !== null ? data.medicareIsSalaryBelowLimit ? yes : no : defaultValue,
          limit: data.medicareMaritalStatus ? this.translate.transform(`common.summary.${data.revision}.salaryBellowLimit.${data.medicareMaritalStatus}`)  : '',
          needCare: data.medicareNeedCare ?
            this.translate.transform(`${formFieldsPrefix}.needCare.${data.medicareNeedCare}.label`) : defaultValue,
          helpCare: data.medicareHelpCare ?
            this.translate.transform(`${formFieldsPrefix}.helpCare.${data.medicareHelpCare}.label`) : defaultValue
        },
        {
          reason: this.translate.transform(`${formFieldsPrefix}.medicareReasons.${data.medicareReason.toLowerCase()}`),
          sep: data.sepReason
            ? this.translate.transform(`${formFieldsPrefix}.sepReasons.${Helpers.snakeToCamel(data.sepReason)}`)
            : defaultValue,
          sepDescr: data.sepReasonDescr || defaultValue,
          effectiveDate: data.coverageEffectiveDate ?
            moment(data.coverageEffectiveDate).format('MM/DD/YYYY') : '',

        },
      ],

      pcp: [
        {
          name: data.pcpName || defaultValue,
          id: data.pcpId || defaultValue,
        },
      ],

      payment: {
        paymentOption: this.translate.transform(`${formFieldsPrefix}.paymentOptionRadio.${data.paymentOption}`),
        benefitsSource: (data.benefitsSource || '').toUpperCase() || defaultValue,
      },
    };
  }

  public readonly medicareSubsections = Helpers.createIterableArray(4);

  @Input()
  set applicationRevision(applicationRevision: string) {
    this.translationPrefixAttribute = `common.summary.${applicationRevision}`;
  }

  resolveAOEReasonDisplay(aoeData, aoeReasonChoices) {
    return aoeData.map((result) => `${aoeReasonChoices[result.reason]} ${result.date ? moment(result.date).format('MM/DD/YYYY') : ''} ${result.description ? `: ` + result.description : ''}<br><br>`).join('');
  }

  get translationPrefix(): string {
    return this.translationPrefixAttribute;
  }

  get showGenderAndIdentityField(){
    return this.featureFlags.isFeatureEnabled(ENABLE_GENDER_AND_IDENTITY_FIELDS);
  }

  get showRelationshipToEnrolleeField(){
    return this.featureFlags.isFeatureEnabled(ENABLE_RELATIONSHIP_TO_ENROLLEE_FIELDS);
  }

  @Output() navigationOccurredEvent = new EventEmitter();
  public readonly applicantInfoTranslationPrefix = 'pages.applicantInfo';
  private translationPrefixAttribute: string;
  public readonly applicantInfoSubsections = Helpers.createIterableArray(5);

  constructor(private translate: TranslatePipe, private featureFlags: FeatureFlagsService) {
  }

  public readonly agreementSubSections = Helpers.createIterableArray(3);

  public emitNavigationOccurred(pageName: string) {
    this.navigationOccurredEvent.emit(AppConstants[`${pageName.toUpperCase()}_PAGE`]);
  }

}
